// Sabine links an answer to the exact playbook item it names (SAL-3285). The
// server's read of the playbook (lib/playbook/corpus.js) writes every anchored
// item as a Markdown link to its element id — "[Oasis Elevator Pitch
// Video](#resource-abc)" — the model copies the links it uses, and the page
// keeps only links to anchors the search returned.
// An answer from the web links the same way, to search results the server
// found, and keeps only those. Only link syntax is matched here; what an item
// means is the model's call.

const ANCHOR_RE = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
// [label](#anchor) and [label](https://…), where the label may hold escaped brackets.
const HASH_LINK_RE = /\[((?:\\.|[^\]\\])*)\]\(#([^)\s]*)\)/g;
const WEB_LINK_RE = /\[((?:\\.|[^\]\\])*)\]\((https?:\/\/[^)\s]+)\)/g;
const MARKDOWN_LINK_RE = /\[((?:\\.|[^\]\\])*)\]\(([^)\s]+)\)/g;

function escapeLabel(text) {
  return String(text).replace(/([\\[\]])/g, "\\$1");
}

function unescapeLabel(label) {
  return label.replace(/\\([\\[\]])/g, "$1");
}

export function isPlaybookAnchor(value) {
  return ANCHOR_RE.test(String(value || ""));
}

/** `text` as a link to `#anchorId`, or unchanged when there is no anchor. */
export function formatPlaybookLink(text, anchorId) {
  if (!isPlaybookAnchor(anchorId)) return text;
  return `[${escapeLabel(text)}](#${anchorId})`;
}

/** Every anchor the text links to, in order of first appearance. */
export function listPlaybookLinkTargets(text) {
  const targets = new Set();
  for (const [, , anchor] of String(text || "").matchAll(HASH_LINK_RE)) {
    if (isPlaybookAnchor(anchor)) targets.add(anchor);
  }
  return [...targets];
}

/** Exact link targets, including resource routes, in first-appearance order. */
export function listMarkdownLinkTargets(text) {
  return [...new Set([...String(text || "").matchAll(MARKDOWN_LINK_RE)].map(([, , href]) => href))];
}

/** The text with each in-page link replaced by its label. */
export function stripPlaybookLinks(text) {
  return String(text || "").replace(HASH_LINK_RE, (_, label) => unescapeLabel(label));
}

/** Keeps links to `knownAnchors` and turns any other in-page link into its label. */
export function keepKnownPlaybookLinks(text, knownAnchors) {
  return String(text || "").replace(HASH_LINK_RE, (link, label, anchor) =>
    knownAnchors.has(anchor) ? link : unescapeLabel(label),
  );
}

/**
 * The text with each in-page link pointed at `target`, for an answer read
 * somewhere other than the playbook it was written on: a page href the
 * anchor is appended to, or a function giving each anchor's href.
 */
export function rebasePlaybookLinks(text, target) {
  const hrefFor = typeof target === "function" ? target : (anchor) => `${target}#${anchor}`;
  return String(text || "").replace(HASH_LINK_RE, (link, label, anchor) =>
    isPlaybookAnchor(anchor) ? `[${label}](${hrefFor(anchor)})` : link,
  );
}

/** `text` as a link to an outside page; parentheses and spaces in it are encoded. */
export function formatWebLink(text, url) {
  return `[${escapeLabel(text)}](${encodeLinkTarget(url)})`;
}

/** A URL as `formatWebLink` writes it, so a link can be matched to its source. */
export function encodeLinkTarget(url) {
  return String(url).replace(/[()\s]/g, (char) =>
    `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`,
  );
}

/** Every outside URL the text links to, in order of first appearance. */
export function listWebLinkTargets(text) {
  return [...new Set([...String(text || "").matchAll(WEB_LINK_RE)].map(([, , url]) => url))];
}

/**
 * Writes each link to a URL in `titlesByUrl` with that page's title as its
 * text, and turns any other outside link into its label.
 */
export function keepListedWebLinks(text, titlesByUrl) {
  return String(text || "").replace(WEB_LINK_RE, (_, label, url) =>
    titlesByUrl.has(url) ? formatWebLink(titlesByUrl.get(url), url) : unescapeLabel(label),
  );
}
